import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { sitesApi } from '../../api/client';
import { CompeteFormPicker } from './CompeteFormPicker';
import type { StudioCaptureForm } from './types';
import type { CompeteFormData } from '../../schemas/components/compete-form.schemas';

interface CompeteFormEditModalProps {
  isOpen: boolean;
  siteId?: string;
  initialData: CompeteFormData;
  isLoading?: boolean;
  onClose: () => void;
  onSave: (data: CompeteFormData) => void;
}

export const CompeteFormEditModal: React.FC<CompeteFormEditModalProps> = ({
  isOpen,
  siteId,
  initialData,
  isLoading = false,
  onClose,
  onSave,
}) => {
  const [forms, setForms] = useState<StudioCaptureForm[]>([]);
  const [linked, setLinked] = useState(true);
  const [loadingForms, setLoadingForms] = useState(false);
  const [selected, setSelected] = useState<CompeteFormData>(initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(initialData);
    if (!siteId) return;

    let cancelled = false;
    setLoadingForms(true);
    setError(null);
    sitesApi
      .getStudioCaptureForms(siteId)
      .then(result => {
        if (cancelled) return;
        setLinked(result.linked);
        setForms(result.forms);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Compete Studio forms.');
      })
      .finally(() => {
        if (!cancelled) setLoadingForms(false);
      });

    return () => {
      cancelled = true;
    };
    // Load once each time the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, siteId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[80]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Compete Studio Form</h2>
          <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          {!linked && (
            <p className="text-sm text-gray-600">
              Link this account to Compete Studio to choose a form.
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {linked && (
            <CompeteFormPicker
              forms={forms}
              selectedToken={selected.shareToken}
              isLoading={loadingForms}
              onSelect={form =>
                setSelected({
                  shareToken: form.shareToken,
                  formName: form.name,
                  desktopHeight: form.desktopHeight,
                  mobileHeight: form.mobileHeight,
                })
              }
            />
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={isLoading || !selected.shareToken}
            onClick={() => onSave(selected)}
          >
            {isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
